import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NyMezo } from '../nav-nyomtatvany.service';

/** Egy ÁNYK-mező helye az adatban: az objektum (lap-példány, láncsor vagy segédtár) és a kulcs. */
export interface AnykHely { obj: any; kod: string; seged?: boolean; }

/**
 *  ÁNYK-LAPKÉP – az .enyk lapjának grafikus képe: keretek, feliratok és a mezők a sablon szerinti
 *  koordinátákon (a régi ÁNYK és a papír nyomtatvány képe). A kitöltő „Nyomtatványkép” nézete és
 *  a nyomtatás is ezt használja. A mezők adatkötését a szülő adja (`hely`), így ugyanazt az adatot
 *  látja, mint a táblázatos nézet és a szabálymotor.
 */
@Component({
  selector: 'app-anyk-lap',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './anyk-lap.component.html',
  styleUrls: ['./anyk-lap.component.scss'],
})
export class AnykLapComponent implements OnChanges {
  @Input() oldal: any;
  /** fid → hely az adatban (null: a sablonban van, de nálunk nincs hova kötni). */
  @Input() hely: (fid: string) => AnykHely | null = () => null;
  /** fid → az XSD-mező (típus, kódlista, szabályok) – ha van. */
  @Input() mezo: (fid: string) => NyMezo | undefined = () => undefined;
  @Input() csakOlvas = false;
  /*  TECHNIKAI (csak ÁNYK-beli) MEZŐK. Amit az XSD nem ismer, azt nem küldjük be – de a lapképhez
   *  tartozhat (pótlapszám, ügyintéző, lapjelzők). Ezek SOSEM tölthetők ki kézzel; alapból csak
   *  akkor látszanak, ha van értékük. A fejlesztői gépen az összes megmutatható (a sablon
   *  elrendezésének ellenőrzéséhez), élesben ez nem kapcsolható be. */
  @Input() technikaiMutat = false;
  @Input() szamitott: Set<string> = new Set();
  /** fid → miért zárt a mező (a számító ÁNYK-szabály üzenete); a buboréksúgóban mutatjuk. */
  @Input() szamitottOk: Map<string, string> = new Map();
  /*  A mező súgója: hiba > „miért zárt" > technikai magyarázat > a mező azonosítója. */
  mezoSugo(e: any): string {
    return this.hiba(e.fid)
      || (this.szamitott.has(e.fid) ? (this.szamitottOk.get(e.fid) || 'Ezt a mezőt a nyomtatvány szabálya tölti ki, ezért nem írható.') : '')
      || (this.technikai(e) ? `Technikai mező – csak a nyomtatványképhez, nem kerül a beküldött adatba (${e.fid})` : e.fid);
  }
  @Input() hibak: Record<string, string> = {};
  @Input() kulcsElotag = '';
  /** A fejléc képhelyére (ÁNYK: címer) kerülő ikon – a modul ikonja. */
  @Input() logo: string | null = null;
  /** Az ÁNYK-sablon mátrixai (kódlisták, pl. közterület jellege, helységnév) és a mezők metaadata. */
  @Input() matrixok: Record<string, string[]> = {};
  @Input() metak: Record<string, any> = {};
  /*  A LAP HÁTTÉRKERETE.
   *  Az ÁNYK-sablonok első kerete a teljes rajzvászon (0,0 – 800×1140), nem a nyomtatvány kerete.
   *  Mivel a lap ennél kisebb, a vászonkeretnek csak a bal és a felső éle látszana – egy furcsa,
   *  félbevágott fekete keret. Ezért az ilyet nem rajzoljuk ki. */
  lapKeret(e: any): boolean {
    return e.x <= 0 && e.y <= 0 && e.w >= (this.oldal?.w || 0) + 20 && e.h >= (this.oldal?.h || 0) + 20;
  }
  @Output() valtozott = new EventEmitter<{ fid: string; m?: NyMezo; ertek: any }>();
  @Output() fokusz = new EventEmitter<{ fid: string; m?: NyMezo }>();

  private helyTar = new Map<string, AnykHely | null>();
  ngOnChanges() { this.helyTar.clear(); this.nyitottKod = null; }

  h(fid: string): AnykHely | null {
    if (!this.helyTar.has(fid)) this.helyTar.set(fid, this.hely(fid));
    return this.helyTar.get(fid)!;
  }
  /** A dátum az ÁNYK-lapon ÉÉÉÉHHNN dobozokban áll, az adatban (és az XML-ben) ÉÉÉÉ-HH-NN. */
  ertek(fid: string): any {
    const h = this.h(fid);
    const v = h ? h.obj[h.kod] ?? '' : '';
    return this.mezo(fid)?.adattipus === 'datum' && typeof v === 'string' ? v.replace(/-/g, '') : v;
  }
  beir(e: any, v: any) {
    const h = this.h(e.fid);
    if (!h) return;
    const m = this.mezo(e.fid);
    let ertek = e.adattipus === 'check' ? (v ? true : null) : v;
    if (m?.adattipus === 'datum' && typeof ertek === 'string' && /^\d{8}$/.test(ertek)) ertek = `${ertek.slice(0, 4)}-${ertek.slice(4, 6)}-${ertek.slice(6, 8)}`;
    /*  KÓDÉRTÉK KIS/NAGYBETŰVEL.
     *  Az ÁNYK-lapon sok kódmező sima szövegdobozként áll (a sablon szerint „ftext"), az XSD
     *  viszont zárt értéklistát ír elő: a beírt „u" helyett a séma „U"-t vár, és a NAV emiatt
     *  dobja vissza az egész bevallást. Ha a beírt érték csak a kis/nagybetűben tér el egy
     *  megengedett kódtól, a séma szerinti alakra igazítjuk. */
    if (m?.adattipus === 'kodlista' && typeof ertek === 'string' && ertek && m.ertekek?.length) {
      const jo = m.ertekek.find((x: any) => String(x.ertek).toLowerCase() === ertek.trim().toLowerCase());
      if (jo) ertek = jo.ertek;
    }
    if (ertek === '' || ertek === null) delete h.obj[h.kod]; else h.obj[h.kod] = ertek;
    this.valtozott.emit({ fid: e.fid, m, ertek });
  }
  /*  Mi látszik: ami az XSD-ben is szerepel (azt töltjük és küldjük be). Az ÁNYK-segédmezők
   *  rejtettek – KIVÉVE, ha van értékük: a papíron látszó, de az XML-be nem kerülő adatok
   *  (pl. „Benyújtott 23-as pótlapok száma", ügyintéző neve) így kitöltve jelennek meg,
   *  csak olvasható módon. */
  lathato(e: any): boolean {
    if (e.lathato === false) return false;
    /*  AZ ÁNYK REJTETT ADATHORDOZÓI. A sablon néhány mezőt 5×5 képpontos dobozban helyez el
     *  (`tatext` típus, pl. „…_torvenyes_jogcim_kod", „betölt_…", „…_tech_torolt"): a papíron
     *  láthatatlanok, a tartalmukat szabály tölti. Nálunk apró, sárga kockákként látszottak a
     *  nyomtatványképen, és a felhasználó kitöltendő mezőnek hitte őket. Fejlesztői nézetben
     *  továbbra is megjelennek. */
    if (this.rejtettAdathordozo(e) && !this.technikaiMutat) return false;
    const h = this.h(e.fid);
    if (!h) return false;
    if (!h.seged) return true;                    // az XSD-ben is meglévő mező: kitölthető, beküldjük
    if (this.technikaiMutat) return true;         // fejlesztői nézet: a technikai mezők is látszanak
    const v = h.obj?.[h.kod];
    return v !== undefined && v !== null && v !== '' && v !== false;
  }
  /** Az ÁNYK 5×5 képpontos, rejtett adathordozó mezője (a papíron sem látszik). */
  rejtettAdathordozo(e: any): boolean {
    if (/tatext/i.test(String(e?.adattipus || '')) || ((e?.w || 0) <= 8 && (e?.h || 0) <= 8)) return true;
    /*  JELKÉPES MEZŐ: a lapon 8 képpontnál keskenyebb (pl. ATVUT17 „Pénzintézet_neve”: 5 px, 80
     *  karakter), vagy 6 képpontnál kisebb karakterdobozokkal rajzolt (az ATVUT17 24 jegyű webes
     *  számlaszáma 3 px-es dobozokkal) – az ÁNYK-ban sem írható, csak adatot hordoz. Nálunk
     *  olvashatatlan, egymásra csúszó dobozsorként jelent meg. A „szekvencia” mezők a sablon belső
     *  sorszámlálói (bankszámlakereső), szintén nem a felhasználónak valók. */
    if ((e?.w || 0) < 8) return true;
    if (/#/.test(String(e?.maszk || '')) && (e?.dobozSzel || 16) < 6) return true;
    if (/^szekvencia/i.test(String(this.metak?.[e?.fid]?.vid || ''))) return true;
    return false;
  }
  /** Technikai mező-e (nincs az XSD-ben, tehát a beküldött XML-be sem kerül). */
  technikai(e: any): boolean { return !!this.h(e.fid)?.seged; }
  tilos(e: any): boolean { return this.csakOlvas || !!e.csakOlvas || this.szamitott.has(e.fid) || !this.h(e.fid) || !!this.h(e.fid)?.seged; }
  /*  A VÁLASZTHATÓ ÉRTÉKEK. Elsősorban az XSD kódlistája; ha ott nincs (pl. a közterület jellege,
   *  a helységnév vagy az országkód szabad szöveg az XSD-ben), az ÁNYK-sablon mátrixa adja. A
   *  mátrixsor „kód=leírás" alakú, de néha a második oszlop csak egy súly-szám – azt nem írjuk ki. */
  private ertekTar = new Map<string, { ertek: string; cim: string | null }[]>();
  ertekek(fid: string): { ertek: string; cim: string | null }[] {
    const m = this.mezo(fid);
    if (m?.ertekek?.length) return m.ertekek;
    // Az ÁNYK-sablon listája olyan mezőkhöz is, amelyek az XSD-ben szabad szövegűek
    // (ország, közterület jellege, helység, FEOR): a kiszolgáló `javasoltErtekek`-ként adja.
    if (m?.javasoltErtekek?.length) return m.javasoltErtekek;
    if (this.ertekTar.has(fid)) return this.ertekTar.get(fid)!;
    const nev = this.metak?.[fid]?.matrix;
    const sorok = nev ? this.matrixok?.[nev] : null;
    const lista = (sorok || []).map((sor) => {
      const r = String(sor).split('=');
      const cim = (r[1] || '').trim();
      return { ertek: (r[0] || '').trim(), cim: cim && !/^[0-9]+$/.test(cim) ? cim : null };
    }).filter((x) => x.ertek);
    if (this.ertekTar.size > 60) this.ertekTar.clear();
    this.ertekTar.set(fid, lista);
    return lista;
  }
  /** Sok elemű lista (helység, ország): legördülő helyett gépelhető mező javaslatokkal. */
  keresheto(fid: string): boolean { return this.ertekek(fid).length > 40; }
  /*  Kódválasztó mező-e. Az ÁNYK több néven is használja ugyanazt („combo", „tcombo", „ncombo"),
   *  és eddig csak a puszta „combo"-t ismertük fel – emiatt maradt lista nélkül például az
   *  ÚJEGYKE „képviselet jogcíme" mezője. */
  komboE(e: any): boolean { return /combo/i.test(String(e?.adattipus || '')); }
  /*  Keskeny kódmező, de SOK választható értékkel: ilyenkor a kattintásra nyíló kis kódlista
   *  kényelmetlen (33 jogcím, több száz TEÁOR), ezért gépelhető mezőt adunk javaslatlistával –
   *  a szám beírható, a megnevezés meg ott van mellette. */
  gepelhetoLista(e: any): boolean { return this.ertekek(e.fid).length > 12; }
  listaAzon(fid: string): string { return 'lst-' + fid; }

  /*  A FELIRAT BELEMÉRETEZÉSE. Az ÁNYK a saját betűmetrikájával tördel, a böngészőé más: emiatt
   *  lógott ki a hosszabb szöveg a keretéből, és – ami rosszabb – a böngésző TÖRDELTE is, így a
   *  második sor rácsúszott az alatta lévő sorra (pl. az ÚJEGYKE „JÖVEDÉKI ÜGYEK" sora).
   *
   *  A szöveg szélességét ezért nem becsüljük karakterszámból (a csupa nagybetűs, ékezetes magyar
   *  szöveg sokkal szélesebb az átlagnál), hanem MEGMÉRJÜK a lap saját betűtípusával, és ehhez
   *  igazítjuk a betűméretet. Ha még a legkisebb megengedett méret sem elég, marad a tördelés –
   *  de a mért érték mellett ez már ritka. */
  feliratMeret(e: any): number {
    const m = Number(e?.betu?.meret) || 10;
    if (!e?.w || !e?.h) return m;
    const sorok = String(e.szoveg || '').split('\n');
    const bold = e.betu?.tipus === 'bold';
    const szeles = m * Math.max(0.001, ...sorok.map((x) => AnykLapComponent.egysegnyiSzeles(x, bold)));
    const magas = sorok.length * m * 1.18;
    //  Lefelé kerekítünk és hagyunk egy hajszálnyi ráhagyást: a böngésző a mért értéknél néha
    //  egy törtpixellel szélesebben rajzol, és pont ez buktatta a határeseteket.
    const arany = Math.min(1, (e.w * 0.985) / szeles, Math.max(e.h, m * 1.2) / magas);
    return Math.max(m * 0.6, Math.floor(m * arany * 10) / 10);
  }

  /*  Egy szöveg szélessége 1 képpontos betűmérettel, a lap betűtípusával. A szélesség a
   *  betűmérettel arányos, ezért elég egyszer, egy referenciaméreten megmérni és skálázni. */
  private static readonly szelessegTar = new Map<string, number>();
  private static meroKtx: CanvasRenderingContext2D | null | undefined;
  private static egysegnyiSzeles(szoveg: string, bold: boolean): number {
    const sz = String(szoveg ?? '');
    if (!sz) return 0.001;
    const kulcs = (bold ? 'b|' : 'n|') + sz;
    const tar = AnykLapComponent.szelessegTar;
    if (tar.has(kulcs)) return tar.get(kulcs)!;
    if (AnykLapComponent.meroKtx === undefined) {
      try { AnykLapComponent.meroKtx = document.createElement('canvas').getContext('2d'); }
      catch { AnykLapComponent.meroKtx = null; }
    }
    const ktx = AnykLapComponent.meroKtx;
    let ertek: number;
    if (ktx) {
      const REF = 100;
      ktx.font = `${bold ? '700 ' : ''}${REF}px Arial, 'Liberation Sans', Helvetica, sans-serif`;
      ertek = (ktx.measureText(sz).width || 1) / REF;
    } else {
      ertek = (bold ? 0.58 : 0.53) * sz.length;      // mérés nélkül (pl. szerveroldali renderelés)
    }
    if (tar.size > 5000) tar.clear();
    tar.set(kulcs, ertek);
    return ertek;
  }
  /*  KARAKTERDOBOZOK az ÁNYK-maszk szerint: '#' = beírandó karakter, betű/számjegy = előnyomott
   *  karakter (pl. a dátum '2026.##.##' évszáma), '.', '-', ' ' = elválasztó (kis rés). A dobozok a
   *  sablon dobozszélességével (char_rect_width, átfedés: char_rect_dist_x) és elválasztó-szélességével
   *  (delimiter_width) állnak; az érték karakterei sorban töltik a dobozokat. '%' maszk: szabad szöveg. */
  private tervTar = new Map<any, { x: number; w: number; elo: string | null }[] | null>();
  /** A maszk kirajzolt elválasztói (pl. az adószám két kötőjele) a dobozok között. */
  private elvTar = new Map<any, { x: number; w: number; jel: string }[]>();
  elvalasztok(e: any): { x: number; w: number; jel: string }[] { this.dobozTerv(e); return this.elvTar.get(e) || []; }
  dobozTerv(e: any): { x: number; w: number; elo: string | null }[] | null {
    if (this.tervTar.has(e)) return this.tervTar.get(e)!;
    let terv: { x: number; w: number; elo: string | null }[] | null = null;
    const elv: { x: number; w: number; jel: string }[] = [];
    const maszk = String(e.maszk || '').split('\\')[0].split('!')[0];
    if (/#/.test(maszk) && e.adattipus !== 'combo' && e.adattipus !== 'check') {
      const dw = e.dobozSzel || 15, tav = e.dobozTav ?? -1, ew = e.elvSzel ?? 3;
      terv = [];
      let x = 0;
      for (const c of maszk) {
        if ('.- /'.includes(c)) { if (c === '-' || c === '/') elv.push({ x: x - Math.min(0, tav), w: ew + Math.max(0, -tav) * 2, jel: c }); x += ew; continue; }
        terv.push({ x, w: dw, elo: c === '#' ? null : c });
        x += dw + tav;
      }
      if (!terv.length) terv = null;
    }
    this.tervTar.set(e, terv);
    this.elvTar.set(e, elv);
    return terv;
  }
  /** A dobozokban megjelenő karakterek (a dátum kötőjel nélkül). */
  dobozKarakter(e: any, i: number): string {
    const v = String(this.ertek(e.fid) ?? '');
    return v[i] ?? (this.dobozTerv(e)![i]?.elo || '');
  }
  /** A beírható (nem előnyomott) dobozok indexei. */
  private szabadTar = new Map<any, number[]>();
  szabadDobozok(e: any): number[] {
    if (!this.szabadTar.has(e)) this.szabadTar.set(e, (this.dobozTerv(e) || []).map((d, i) => (d.elo ? -1 : i)).filter((i) => i >= 0));
    return this.szabadTar.get(e)!;
  }
  /** A beviteli mező tartalma: csak a szabad dobozok karakterei (az előnyomottat nem kell gépelni). */
  bevitel(e: any): string {
    const v = String(this.ertek(e.fid) ?? '');
    const terv = this.dobozTerv(e) || [];
    const vanElo = terv.some((d) => d.elo);
    return vanElo ? this.szabadDobozok(e).map((i) => v[i] ?? '').join('') : v;
  }
  /** A begépelt karakterek + az előnyomott dobozok → az érték. */
  dobozBe(e: any, begepelt: string) {
    const terv = this.dobozTerv(e) || [];
    if (!terv.some((d) => d.elo)) { this.beir(e, begepelt); return; }
    const b = String(begepelt || '');
    if (!b) { this.beir(e, ''); return; }
    let j = 0;
    const kar = terv.map((d) => (d.elo ? d.elo : (b[j++] ?? '')));
    // csak az utolsó beírt karakterig (a hiányos érték a validációnál jelez)
    let utolso = -1;
    this.szabadDobozok(e).forEach((i, k) => { if (k < b.length) utolso = i; });
    this.beir(e, kar.slice(0, utolso + 1).join(''));
  }
  /** A kurzor dobozának indexe (a következő beírandó karakter helye). */
  aktivDoboz(e: any): number {
    if (this.fokuszban !== e) return -1;
    const sz = this.szabadDobozok(e);
    return sz[Math.min(this.bevitel(e).length, sz.length - 1)] ?? -1;
  }
  fokuszban: any = null;
  hiba(fid: string): string | null { return this.hibak[this.kulcsElotag + fid] || null; }
  /** X-mező billentyűi: szóköz / X kapcsol, Delete / Backspace töröl. */
  xBill(ev: KeyboardEvent, e: any) {
    if (this.tilos(e)) return;
    const k = ev.key.toLowerCase();
    if (k === ' ' || k === 'x') { ev.preventDefault(); this.beir(e, !(this.ertek(e.fid) === true || this.ertek(e.fid) === 'true')); }
    else if (k === 'delete' || k === 'backspace') { ev.preventDefault(); this.beir(e, false); }
  }
  /* ── jelölőként működő kódmező ── */
  nyitottKod: any = null;
  private egyErtekes(fid: string): string | null { const l = this.ertekek(fid); return l.length === 1 ? l[0].ertek : null; }
  /** Az egy értékű kódmező X-et mutat, a többértékű a választott kódot. */
  kodJel(e: any): string { const v = this.ertek(e.fid); if (!v) return ''; return this.egyErtekes(e.fid) ? 'X' : String(v); }
  kodKattint(e: any) {
    if (this.tilos(e)) return;
    const egy = this.egyErtekes(e.fid);
    if (egy) { this.beir(e, this.ertek(e.fid) ? '' : egy); return; }
    this.nyitottKod = this.nyitottKod === e ? null : e;
  }
  /*  A kódlista bal széle. Ha a mezőtől jobbra már nem férne el a papíron, balra nyílik –
   *  a listát így sem vágja el semmi (a papíron kívül rajzoljuk), de fölöslegesen nem lóg ki. */
  kodBal(e: any): number {
    const papir = (this.oldal?.w || 0) + 20;
    return Math.max(0, Math.min(e.x, papir - AnykLapComponent.KODLISTA_SZELES));
  }
  private static readonly KODLISTA_SZELES = 260;   // a .anyk-kodlista tipikus szélessége (min 180, max 360)
  /*  A lenyíló tételei. Hosszú listánál (TEÁOR: több száz tétel) a mezőbe már beírt szöveggel
   *  szűkítünk – kódra és megnevezésre egyaránt –, és csak az első adagot mutatjuk. */
  private static readonly KODLISTA_MAX = 60;
  kodListaTetelek(e: any): { ertek: string; cim: string | null }[] {
    return this.kodListaSzurt(e).slice(0, AnykLapComponent.KODLISTA_MAX);
  }
  kodListaTobbVan(e: any): boolean { return this.kodListaSzurt(e).length > AnykLapComponent.KODLISTA_MAX; }
  private kodListaSzurt(e: any): { ertek: string; cim: string | null }[] {
    const mind = this.ertekek(e.fid);
    const sz = String(this.ertek(e.fid) ?? '').trim().toLowerCase();
    if (!sz || mind.length <= AnykLapComponent.KODLISTA_MAX) return mind;
    const talal = mind.filter((v) => v.ertek.toLowerCase().startsWith(sz) || String(v.cim || '').toLowerCase().includes(sz));
    return talal.length ? talal : mind;
  }
  kodValaszt(e: any, v: string) { this.beir(e, v); this.nyitottKod = null; }
  kodZar(e: any) { setTimeout(() => { if (this.nyitottKod === e) this.nyitottKod = null; }, 150); }
  kodBill(ev: KeyboardEvent, e: any) {
    if (this.tilos(e)) return;
    const k = ev.key;
    if (k === ' ' || k === 'Enter') { ev.preventDefault(); this.kodKattint(e); return; }
    if (k === 'Delete' || k === 'Backspace') { ev.preventDefault(); this.beir(e, ''); return; }
    if (k === 'Escape') { this.nyitottKod = null; return; }
    // a kód billentyűje közvetlenül is választ (pl. „2”), az egy értékűnél az X is
    const talalt = this.ertekek(e.fid).find((x) => x.ertek.toLowerCase() === k.toLowerCase());
    if (talalt) { ev.preventDefault(); this.kodValaszt(e, talalt.ertek); }
    else if (k.toLowerCase() === 'x' && this.egyErtekes(e.fid)) { ev.preventDefault(); this.kodValaszt(e, this.egyErtekes(e.fid)!); }
  }
  kodSugo(fid: string): string { const l = this.ertekek(fid); return l.length ? 'Megengedett: ' + l.map((x) => x.cim ? `${x.ertek} – ${x.cim}` : x.ertek).join(', ') : fid; }
  trk(i: number) { return i; }
}
